import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAutocomplete, type AutocompleteSuggestion } from "@/hooks/useSearch";
import { trackAutocompleteSelect } from "@/analytics/analytics";

const CATEGORY_COLORS: Record<number, string> = {
  0: "text-tag-general",
  1: "text-tag-artist",
  3: "text-tag-copyright",
  4: "text-tag-character",
  5: "text-tag-species",
  6: "text-tag-meta",
  8: "text-tag-lore",
};

const CATEGORY_NAMES: Record<number, string> = {
  0: "general",
  1: "artist",
  3: "copyright",
  4: "character",
  5: "species",
  6: "meta",
  8: "lore",
};

// Lets users type `artist:rouge` and scope the dropdown to category 1 in one
// keystroke. Mapped against the e621 category IDs used elsewhere.
const CATEGORY_PREFIX_TO_INT: Record<string, number> = {
  general: 0,
  artist: 1,
  copyright: 3,
  character: 4,
  species: 5,
  meta: 7,
  lore: 8,
};

interface ParsedToken {
  prefix: string;
  category?: number;
  replaceStart: number;
  categoryPrefix: string;
  modifier: string;
}

function parseLastToken(value: string, tokenPattern: RegExp): ParsedToken {
  // ``tokenPattern`` must match the trailing run of non-separator characters,
  // anchored to end of string. Provided by the parent so this component is
  // separator-agnostic (whitespace, or whitespace + commas, etc.).
  const m = tokenPattern.exec(value);
  const token = m ? m[0] : "";
  const replaceStart = m ? m.index : value.length;

  let modifier = "";
  let body = token;
  if (body.startsWith("-") || body.startsWith("~")) {
    modifier = body[0];
    body = body.slice(1);
  }

  const colon = body.indexOf(":");
  if (colon > 0) {
    const key = body.slice(0, colon).toLowerCase();
    const after = body.slice(colon + 1);
    const cat = CATEGORY_PREFIX_TO_INT[key];
    if (cat !== undefined) {
      return {
        prefix: after,
        category: cat,
        replaceStart,
        categoryPrefix: `${key}:`,
        modifier,
      };
    }
  }

  return { prefix: body, replaceStart, categoryPrefix: "", modifier };
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Called when the user presses Enter and no suggestion is highlighted. */
  onCommit?: () => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  /** Regex matching the trailing run of non-separator chars at end of string.
   *  Defaults to whitespace-separated tokens (`/\S+$/`). For
   *  comma-or-whitespace separators use `/[^\s,]+$/`. */
  tokenPattern?: RegExp;
  /** Forwarded to the input element so consumers can style via parent CSS. */
  inputClassName?: string;
}

export function TagAutocomplete({
  value,
  onChange,
  onCommit,
  placeholder,
  className = "",
  autoFocus = false,
  tokenPattern = /\S+$/,
  inputClassName = "",
}: Props) {
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const refreshSuggestions = useCallback(
    (q: string) => {
      const parsed = parseLastToken(q, tokenPattern);
      if (parsed.prefix.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        const results = await fetchAutocomplete(parsed.prefix, 8, parsed.category);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setSelectedIdx(-1);
      }, 150);
    },
    [tokenPattern]
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    onChange(v);
    refreshSuggestions(v);
  }

  function applySuggestion(name: string, category: number) {
    const parsed = parseLastToken(value, tokenPattern);
    const before = value.slice(0, parsed.replaceStart);
    const next = before + parsed.modifier + parsed.categoryPrefix + name + " ";
    onChange(next);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
    trackAutocompleteSelect(CATEGORY_NAMES[category] ?? "unknown");
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      if (selectedIdx >= 0 && suggestions[selectedIdx]) {
        e.preventDefault();
        const s = suggestions[selectedIdx];
        applySuggestion(s.name, s.category);
      } else if (onCommit) {
        e.preventDefault();
        setShowSuggestions(false);
        onCommit();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  }

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        placeholder={placeholder}
        className={inputClassName}
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-haspopup="listbox"
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-surface-1 border border-surface-3 rounded-lg shadow-xl overflow-y-auto max-h-[60vh]"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.name}
              role="option"
              aria-selected={i === selectedIdx}
              className={`flex items-start justify-between gap-3 px-4 py-2 cursor-pointer text-sm transition-colors ${
                i === selectedIdx ? "bg-surface-2" : "hover:bg-surface-2"
              }`}
              onMouseDown={() => applySuggestion(s.name, s.category)}
              title={
                s.matched_alias
                  ? `Matched alias "${s.matched_alias}" → resolved to canonical "${s.name}"`
                  : s.implies.length > 0
                    ? `Also matches: ${s.implies.join(", ")}`
                    : undefined
              }
            >
              <div className="min-w-0 flex-1">
                <span className={CATEGORY_COLORS[s.category] ?? "text-zinc-300"}>
                  {s.name}
                </span>
                {s.matched_alias && (
                  <div className="text-[10px] text-amber-500/80 mt-0.5 truncate">
                    ↩ via {s.matched_alias}
                  </div>
                )}
                {s.implies.length > 0 && (
                  <div className="text-[10px] text-zinc-600 mt-0.5 truncate">
                    ↳ {s.implies.slice(0, 4).join(", ")}
                    {s.implies.length > 4 && ` +${s.implies.length - 4}`}
                  </div>
                )}
              </div>
              <span className="text-zinc-600 text-xs shrink-0 mt-0.5">
                {s.post_count.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
